<script setup lang="ts">
import { ref, computed } from 'vue'

export interface SubmitRequestModalProps {
  modelValue: boolean
  type?: 'fanpage' | 'group' | 'ad' | 'profile' | 'All'
}

const props = withDefaults(defineProps<SubmitRequestModalProps>(), {
  type: 'All',
})

const emit = defineEmits<{
  (event: 'update:modelValue', value: boolean): void
  (event: 'submit', data: { email: string; url: string; type: string }): void
}>()

const email = ref('')
const url = ref('')
const submitting = ref(false)
const error = ref<string | null>(null)

const showModal = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

const getTypeLabel = () => {
  const typeMap: Record<string, string> = {
    fanpage: 'Fanpage',
    group: 'Group',
    ad: 'Sponsored Ad',
    profile: 'Profile',
    All: 'Audience',
  }
  return typeMap[props.type] || 'Audience'
}

const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

const validateUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url)
    return urlObj.hostname.includes('facebook.com') || urlObj.hostname.includes('fb.com')
  } catch {
    return false
  }
}

const handleSubmit = async () => {
  error.value = null

  // Validation
  if (!email.value.trim()) {
    error.value = 'Email is required'
    return
  }

  if (!validateEmail(email.value)) {
    error.value = 'Please enter a valid email address'
    return
  }

  if (!url.value.trim()) {
    error.value = 'Page/Group URL is required'
    return
  }

  if (!validateUrl(url.value)) {
    error.value = 'Please enter a valid Facebook page or group URL'
    return
  }

  submitting.value = true

  try {
    emit('submit', {
      email: email.value.trim(),
      url: url.value.trim(),
      type: props.type === 'All' ? 'fanpage' : props.type, // Default to fanpage if All
    })

    // Reset form
    email.value = ''
    url.value = ''
    showModal.value = false
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to submit request'
  } finally {
    submitting.value = false
  }
}

const handleCancel = () => {
  email.value = ''
  url.value = ''
  error.value = null
  showModal.value = false
}
</script>

<template>
  <VaModal
    v-model="showModal"
    :title="`Submit New ${getTypeLabel()} Request`"
    size="medium"
    :ok-text="submitting ? 'Submitting...' : 'Submit Request'"
    cancel-text="Cancel"
    :ok-disabled="submitting"
    @ok="handleSubmit"
    @cancel="handleCancel"
  >
    <div class="space-y-4">
      <div class="bg-info-light rounded-lg p-4 mb-4">
        <div class="flex items-start gap-2">
          <VaIcon name="info" color="info" />
          <div class="text-sm text-secondary">
            <p class="font-semibold mb-1">What you need:</p>
            <ul class="list-disc list-inside space-y-1">
              <li>Your email address for notifications</li>
              <li>The Facebook page or group URL you want to request audience data for</li>
            </ul>
          </div>
        </div>
      </div>

      <VaInput
        v-model="email"
        label="Email"
        type="email"
        placeholder="your.email@example.com"
        :rules="[() => email.trim() || 'Email is required']"
        required
        class="mb-4"
      >
        <template #prependInner>
          <VaIcon name="email" color="secondary" size="small" />
        </template>
      </VaInput>

      <VaInput
        v-model="url"
        label="Facebook Page/Group URL"
        type="url"
        placeholder="https://www.facebook.com/your-page-or-group"
        :rules="[() => url.trim() || 'URL is required']"
        required
        class="mb-4"
      >
        <template #prependInner>
          <VaIcon name="link" color="secondary" size="small" />
        </template>
      </VaInput>

      <VaAlert v-if="error" color="danger" class="mb-4">
        {{ error }}
      </VaAlert>

      <div class="text-xs text-secondary text-center mt-4">
        By submitting this request, you agree that we will process your request and notify you via email when the
        audience data is ready.
      </div>
    </div>
  </VaModal>
</template>
